import { useState, useEffect, useCallback } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Typography,
  Alert,
  MenuItem,
  Box,
  Collapse,
  FormControlLabel,
  Switch,
  Tooltip,
  Snackbar,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  NotificationsActive as NotificationsActiveIcon,
  NotificationsOff as NotificationsOffIcon,
  Webhook as WebhookIcon,
  Settings as SettingsIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import { botService } from "@/services/api";
import { useDialog } from "@/hooks/useDialog";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { BOT_TYPES, API_ENDPOINTS } from "@/utils/constants";
import { PageContainer, ToolbarContainer } from "./styles";

const DEFAULT_FORM_DATA = {
  bot_name: "",
  bot_type: "",
  bot_callback_id: "",
  bot_token: "",
  bot_comment: "",
  bot_secret_key_1: "",
  bot_secret_key_2: "",
  bot_secret_key_3: "",
  notify_enabled: true, // 預設為 true
};

const Bots = () => {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [selectedBot, setSelectedBot] = useState(null);

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [siteUrl, setSiteUrl] = useState("");

  const {
    open: openDialog,
    handleOpen: handleOpenDialog,
    handleClose: handleCloseDialog,
  } = useDialog();

  const {
    open: openConfirm,
    data: confirmData,
    handleOpen: handleOpenConfirm,
    handleClose: handleCloseConfirm,
  } = useDialog();

  const fetchBots = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await botService.getBots();
      setBots(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setSiteUrl(window.location.origin);
  }, []);

  useEffect(() => {
    fetchBots();
  }, [fetchBots]);

  const handleEdit = (bot) => {
    setSelectedBot(bot);
    setFormData({
      bot_name: bot.bot_name,
      bot_type: bot.bot_type,
      bot_callback_id: bot.bot_callback_id,
      bot_token: bot.bot_token,
      bot_comment: bot.bot_comment || "",
      bot_secret_key_1: bot.bot_secret_key_1 || "",
      bot_secret_key_2: bot.bot_secret_key_2 || "",
      bot_secret_key_3: bot.bot_secret_key_3 || "",
      notify_enabled: bot.notify_enabled !== false, // 如果 undefined 則預設為 true
    });
    handleOpenDialog();
  };

  const handleCreate = () => {
    setSelectedBot(null);
    setFormData(DEFAULT_FORM_DATA);
    handleOpenDialog();
  };

  const handleSubmit = async () => {
    try {
      if (selectedBot) {
        await botService.updateBot(selectedBot.id, formData);
      } else {
        await botService.createBot(formData);
      }
      handleCloseDialog();
      fetchBots();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = (id) => {
    handleOpenConfirm({
      id,
      title: "確認刪除",
      message: "確定要刪除此機器人嗎？此操作無法復原。",
    });
  };

  const handleConfirmDelete = async () => {
    try {
      await botService.deleteBot(confirmData.id);
      handleCloseConfirm();
      fetchBots();
    } catch (err) {
      setError(err.message);
    }
  };

  // 設定Telegram Webhook
  const handleSetWebhook = async (bot) => {
    if (bot.bot_type !== "TELEGRAM") return;

    try {
      setLoading(true);

      // 取得callback URL
      const callbackUrl = `${siteUrl}/api/bots/telegram/callback/${bot.bot_callback_id}`;

      // 使用 botService 方法
      const response = await botService.setTelegramWebhook(
        bot.bot_callback_id,
        callbackUrl
      );

      // 顯示成功訊息
      if (response.status === "success") {
        setNotification({
          open: true,
          message: `Webhook 設定成功! ${callbackUrl}`,
          severity: "success",
        });
      } else {
        setNotification({
          open: true,
          message: `Webhook 設定失敗: ${response.message}`,
          severity: "error",
        });
      }
    } catch (err) {
      setNotification({
        open: true,
        message: `Webhook 設定錯誤: ${err.message}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // 複製功能
  const handleCopyToClipboard = (text, message) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setNotification({
          open: true,
          message: message || "已複製到剪貼簿",
          severity: "success",
        });
      },
      (err) => {
        setNotification({
          open: true,
          message: "複製失敗: " + err,
          severity: "error",
        });
      }
    );
  };

  // 格式化轉派權杖
  const formatRelayToken = (bot) => {
    const prefix = bot.bot_type === "LINE" ? "LE" : "TG";
    return `${prefix}${bot.bot_callback_id}00000`;
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "bot_name", headerName: "機器人名稱", width: 150 },
    { field: "bot_type", headerName: "類型", width: 100 },
    {
      field: "notify_enabled",
      headerName: "通知",
      width: 80,
      renderCell: (params) => {
        // 預設為 true（如果值是 undefined）
        const isEnabled = params.value !== false;
        return (
          <Tooltip title={isEnabled ? "通知已啟用" : "通知已停用"}>
            <IconButton
              size="small"
              onClick={() => handleToggleNotify(params.row)}
              color={isEnabled ? "primary" : "default"}
            >
              {isEnabled ? (
                <NotificationsActiveIcon />
              ) : (
                <NotificationsOffIcon />
              )}
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: "bot_callback_id",
      headerName: "轉派權杖",
      width: 220,
      valueGetter: (params) => formatRelayToken(params.row),
      renderCell: (params) => {
        const relayToken = params.value;
        return (
          <Tooltip title={relayToken} placement="top">
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
              <Typography
                sx={{
                  mr: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flexGrow: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {relayToken}
              </Typography>
              <Tooltip title="複製轉派權杖">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyToClipboard(relayToken, "轉派權杖已複製到剪貼簿");
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Tooltip>
        );
      },
    },

    {
      field: "callback_url",
      headerName: "Callback URL",
      width: 550, // 增加寬度以適應長 URL
      valueGetter: (params) => {
        const origin = window.location.origin;
        return `${origin}/api/bots/${params.row.bot_type.toLowerCase()}${
          API_ENDPOINTS.CALLBACK
        }/${params.row.bot_callback_id}`;
      },
      renderCell: (params) => {
        const callbackUrl = params.value;
        return (
          <Tooltip title={callbackUrl} placement="top">
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
              <Typography
                sx={{
                  mr: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flexGrow: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {callbackUrl}
              </Typography>
              <Tooltip title="複製 Callback URL">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyToClipboard(
                      callbackUrl,
                      "Callback URL 已複製到剪貼簿"
                    );
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Tooltip>
        );
      },
    },
    { field: "bot_comment", headerName: "備註", width: 120 },
    {
      field: "actions",
      headerName: "操作",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            title="編輯"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.id)}
            title="刪除"
          >
            <DeleteIcon />
          </IconButton>
          {/* Telegram 機器人專用的 Webhook 設定按鈕 */}
          {params.row.bot_type === "TELEGRAM" && (
            <IconButton
              size="small"
              onClick={() => handleSetWebhook(params.row)}
              title="設定 Telegram Webhook"
              color="secondary"
            >
              <SettingsIcon />
            </IconButton>
          )}
        </>
      ),
    },
  ];

  if (loading && !bots.length) {
    return <LoadingSpinner />;
  }

  return (
    <PageContainer>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <ToolbarContainer>
        <Typography variant="h6">機器人管理</Typography>
        <Button variant="contained" color="primary" onClick={handleCreate}>
          新增機器人
        </Button>
      </ToolbarContainer>

      <DataGrid
        rows={bots}
        columns={columns}
        autoHeight
        loading={loading}
        slots={{
          toolbar: GridToolbar,
        }}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
      />

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedBot ? "編輯機器人" : "新增機器人"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="normal"
            label="機器人名稱"
            fullWidth
            value={formData.bot_name}
            onChange={(e) =>
              setFormData({ ...formData, bot_name: e.target.value })
            }
          />
          <TextField
            select
            margin="normal"
            label="類型"
            fullWidth
            value={formData.bot_type}
            disabled={!!selectedBot} // 當 selectedBot 存在時禁用
            onChange={(e) =>
              setFormData({ ...formData, bot_type: e.target.value })
            }
          >
            {Object.entries(BOT_TYPES).map(([key, value]) => (
              <MenuItem key={key} value={value}>
                {value}
              </MenuItem>
            ))}
          </TextField>
          {selectedBot && (
            <TextField
              margin="normal"
              label="Callback ID"
              fullWidth
              value={formData.bot_callback_id}
              InputProps={{
                readOnly: true,
              }}
              disabled={!!selectedBot} // 當 selectedBot 存在時禁用
            />
          )}
          <TextField
            margin="normal"
            label="Token"
            fullWidth
            value={formData.bot_token}
            onChange={(e) =>
              setFormData({ ...formData, bot_token: e.target.value })
            }
          />

          <TextField
            margin="normal"
            label="Secret Key"
            fullWidth
            value={formData.bot_secret_key_1}
            onChange={(e) =>
              setFormData({ ...formData, bot_secret_key_1: e.target.value })
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.notify_enabled !== false}
                onChange={(e) =>
                  setFormData({ ...formData, notify_enabled: e.target.checked })
                }
                color="primary"
              />
            }
            label="發送通知"
            sx={{ mt: 2 }}
          />
          <TextField
            margin="normal"
            label="備註"
            fullWidth
            multiline
            rows={2}
            value={formData.bot_comment}
            onChange={(e) =>
              setFormData({ ...formData, bot_comment: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>取消</Button>
          <Button onClick={handleSubmit} color="primary">
            確定
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={openConfirm}
        title={confirmData?.title}
        message={confirmData?.message}
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseConfirm}
      />

      {/* 通知訊息 */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default Bots;
